import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FileItem } from '../services/storageService';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';

interface AudioEditorProps {
  file: FileItem;
  onSave: (blob: Blob, filename: string) => void;
  onCancel: () => void;
  defaultTab?: AudioTool;
}

type AudioTool = 'tts' | 'demix' | 'rvc' | 'transcribe' | 'speech-translate' | 'effects';

interface StemBlob {
  name: string;
  url: string;
  blob: Blob;
}

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ar', label: 'العربية' },
];

// Langues SeamlessM4T v2 (codes ISO 639-3 subset parlé+chant)
const SEAMLESS_LANGUAGES = [
  { code: 'fra', label: 'Français' },
  { code: 'eng', label: 'English' },
  { code: 'spa', label: 'Español' },
  { code: 'deu', label: 'Deutsch' },
  { code: 'ita', label: 'Italiano' },
  { code: 'por', label: 'Português' },
  { code: 'nld', label: 'Nederlands' },
  { code: 'pol', label: 'Polski' },
  { code: 'rus', label: 'Русский' },
  { code: 'cmn', label: '普通话 (Mandarin)' },
  { code: 'jpn', label: '日本語' },
  { code: 'arb', label: 'العربية' },
  { code: 'kor', label: '한국어' },
  { code: 'tur', label: 'Türkçe' },
  { code: 'hin', label: 'हिन्दी' },
  { code: 'ben', label: 'বাংলা' },
  { code: 'vie', label: 'Tiếng Việt' },
  { code: 'ind', label: 'Bahasa Indonesia' },
];

const XTTS_SPEAKERS = [
  'Claribel Dervla', 'Daisy Studious', 'Gracie Wise', 'Tammie Ema',
  'Alison Dietlinde', 'Ana Florence', 'Annmarie Nele', 'Asya Anara',
  'Brenda Stern', 'Gitta Nikolina', 'Henriette Usha', 'Sofia Hellen',
  'Tammy Grit', 'Tanja Adelina', 'Vjollca Johnnie', 'Andrew Chipper',
  'Badr Odhiambo', 'Dionisio Schuyler', 'Royston Min', 'Viktor Eka',
  'Abrahan Mack', 'Adde Michal', 'Baldur Sanjin', 'Craig Gutsy',
  'Damien Black', 'Gilberto Mathias', 'Ilkin Urbano', 'Kazuhiko Atallah',
  'Ludvig Milivoj', 'Suad Qasim', 'Torcull Diarmuid', 'Viktor Menelaos',
  'Zacharie Aimilios', 'Nova Hogarth', 'Maja Ruoho', 'Uta Obando',
  'Lidiya Szekeres', 'Chandra MacFarland', 'Szofi Granger',
];

// ── Effets : types, catalogue, helpers ────────────────────────────────────────
interface FxEffect {
  id: string;
  type: string;
  enabled: boolean;
  params: Record<string, number>;
}

interface FxRegion {
  id: string;
  start: number;
  end: number;
  color: string;
  label: string;
  effects: FxEffect[];
}

const REGION_COLORS = [
  'rgba(99,102,241,0.3)', 'rgba(236,72,153,0.3)', 'rgba(234,179,8,0.3)',
  'rgba(34,197,94,0.3)',  'rgba(239,68,68,0.3)',  'rgba(14,165,233,0.3)',
];

interface FxParamSpec { label: string; min: number; max: number; step: number; default: number; }
interface FxEffectDef { label: string; icon: string; params: Record<string, FxParamSpec>; }

const FX_CATALOG: Record<string, FxEffectDef> = {
  gain:       { label: 'Volume',             icon: '🔊', params: { gain:      { label: 'Gain (%)',          min: 0,    max: 2,    step: 0.01, default: 1 } } },
  fadeIn:     { label: 'Fade In',            icon: '↗',  params: { duration:  { label: 'Durée (s)',         min: 0.1,  max: 10,   step: 0.1,  default: 1 } } },
  fadeOut:    { label: 'Fade Out',           icon: '↘',  params: { duration:  { label: 'Durée (s)',         min: 0.1,  max: 10,   step: 0.1,  default: 1 } } },
  eq:         { label: 'Égaliseur 3 bandes', icon: '🎚', params: {
    bass:   { label: 'Basses (dB)',  min: -12, max: 12, step: 0.5, default: 0 },
    mid:    { label: 'Médiums (dB)', min: -12, max: 12, step: 0.5, default: 0 },
    treble: { label: 'Aigus (dB)',   min: -12, max: 12, step: 0.5, default: 0 },
  }},
  compressor: { label: 'Compresseur',        icon: '🏔', params: {
    threshold: { label: 'Seuil (dB)', min: -60, max: 0,  step: 1,   default: -24 },
    ratio:     { label: 'Ratio',      min: 1,   max: 20, step: 0.5, default: 4   },
  }},
  reverb:     { label: 'Réverbération',      icon: '🌊', params: {
    decay: { label: 'Durée (s)', min: 0.1, max: 5,   step: 0.1,  default: 1.5 },
    wet:   { label: 'Dosage',   min: 0,   max: 1,   step: 0.01, default: 0.3 },
  }},
  delay:      { label: 'Écho / Delay',       icon: '🔄', params: {
    time:     { label: 'Délai (s)',  min: 0.05, max: 2,   step: 0.01, default: 0.3 },
    feedback: { label: 'Feedback',  min: 0,    max: 0.9, step: 0.01, default: 0.3 },
    mix:      { label: 'Dosage',    min: 0,    max: 1,   step: 0.01, default: 0.3 },
  }},
  distortion: { label: 'Distorsion',         icon: '📻', params: { amount: { label: 'Intensité', min: 0, max: 400, step: 1, default: 50 } } },
  pan:        { label: 'Panoramique',         icon: '↔',  params: { pan: { label: 'Pan (G←→D)', min: -1, max: 1, step: 0.01, default: 0 } } },
  lowpass:    { label: 'Filtre passe-bas',    icon: '🔽', params: { frequency: { label: 'Fréquence (Hz)', min: 100, max: 20000, step: 50,  default: 5000 } } },
  highpass:   { label: 'Filtre passe-haut',   icon: '🔼', params: { frequency: { label: 'Fréquence (Hz)', min: 20,  max: 8000,  step: 10,  default: 200  } } },
  normalize:  { label: 'Normaliser',          icon: '📊', params: { target: { label: 'Niveau cible (dBFS)', min: -20, max: 0, step: 0.5, default: -1 } } },
};

function buildFxChain(
  ctx: BaseAudioContext,
  source: AudioBufferSourceNode,
  chain: FxEffect[],
  buffer: AudioBuffer,
  trimStart: number,
  trimEnd: number,
  t0: number,
): AudioNode {
  let node: AudioNode = source;
  const duration = trimEnd - trimStart;
  for (const fx of chain) {
    if (!fx.enabled) continue;
    switch (fx.type) {
      case 'gain': {
        const g = ctx.createGain(); g.gain.value = fx.params.gain;
        node.connect(g); node = g; break;
      }
      case 'fadeIn': {
        const g = ctx.createGain();
        const d = Math.min(fx.params.duration, duration);
        g.gain.setValueAtTime(0, t0); g.gain.linearRampToValueAtTime(1, t0 + d);
        node.connect(g); node = g; break;
      }
      case 'fadeOut': {
        const g = ctx.createGain();
        const d = Math.min(fx.params.duration, duration);
        g.gain.setValueAtTime(1, t0 + Math.max(0, duration - d));
        g.gain.linearRampToValueAtTime(0, t0 + duration - 0.01);
        node.connect(g); node = g; break;
      }
      case 'eq': {
        const bass = ctx.createBiquadFilter();
        bass.type = 'lowshelf'; bass.frequency.value = 200; bass.gain.value = fx.params.bass;
        const mid = ctx.createBiquadFilter();
        mid.type = 'peaking'; mid.frequency.value = 1000; mid.Q.value = 1; mid.gain.value = fx.params.mid;
        const treble = ctx.createBiquadFilter();
        treble.type = 'highshelf'; treble.frequency.value = 4000; treble.gain.value = fx.params.treble;
        node.connect(bass); bass.connect(mid); mid.connect(treble); node = treble; break;
      }
      case 'compressor': {
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = fx.params.threshold; comp.ratio.value = fx.params.ratio;
        node.connect(comp); node = comp; break;
      }
      case 'reverb': {
        const len = Math.floor(ctx.sampleRate * fx.params.decay);
        const ir = ctx.createBuffer(2, len, ctx.sampleRate);
        for (let ch = 0; ch < 2; ch++) {
          const d = ir.getChannelData(ch);
          for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
        }
        const conv = ctx.createConvolver(); conv.buffer = ir;
        const dryG = ctx.createGain(); dryG.gain.value = 1 - fx.params.wet;
        const wetG = ctx.createGain(); wetG.gain.value = fx.params.wet;
        const out = ctx.createGain();
        node.connect(dryG); node.connect(conv); conv.connect(wetG);
        dryG.connect(out); wetG.connect(out); node = out; break;
      }
      case 'delay': {
        const del = ctx.createDelay(5); del.delayTime.value = fx.params.time;
        const fb = ctx.createGain(); fb.gain.value = fx.params.feedback;
        const dryG = ctx.createGain(); dryG.gain.value = 1 - fx.params.mix;
        const wetG = ctx.createGain(); wetG.gain.value = fx.params.mix;
        const out = ctx.createGain();
        node.connect(dryG); node.connect(del);
        del.connect(fb); fb.connect(del); del.connect(wetG);
        dryG.connect(out); wetG.connect(out); node = out; break;
      }
      case 'distortion': {
        const ws = ctx.createWaveShaper();
        const amt = fx.params.amount;
        const curve = new Float32Array(256);
        for (let i = 0; i < 256; i++) {
          const x = (i * 2) / 256 - 1;
          curve[i] = ((Math.PI + amt) * x) / (Math.PI + amt * Math.abs(x));
        }
        ws.curve = curve; ws.oversample = '4x';
        node.connect(ws); node = ws; break;
      }
      case 'pan': {
        const p = ctx.createStereoPanner(); p.pan.value = fx.params.pan;
        node.connect(p); node = p; break;
      }
      case 'lowpass': {
        const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = fx.params.frequency;
        node.connect(f); node = f; break;
      }
      case 'highpass': {
        const f = ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = fx.params.frequency;
        node.connect(f); node = f; break;
      }
      case 'normalize': {
        let peak = 0;
        for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
          const d = buffer.getChannelData(ch);
          for (let i = 0; i < d.length; i++) if (Math.abs(d[i]) > peak) peak = Math.abs(d[i]);
        }
        const g = ctx.createGain();
        g.gain.value = peak > 0 ? Math.pow(10, fx.params.target / 20) / peak : 1;
        node.connect(g); node = g; break;
      }
    }
  }
  return node;
}

async function renderWithRegionEffects(
  buffer: AudioBuffer,
  regions: FxRegion[],
  globalChain: FxEffect[],
  fromTime: number,
  toTime: number,
): Promise<AudioBuffer> {
  const sr = buffer.sampleRate;
  const nCh = buffer.numberOfChannels;

  // Build non-overlapping segments, sorted by start
  const sorted = [...regions]
    .filter(r => r.end > fromTime && r.start < toTime)
    .sort((a, b) => a.start - b.start);

  const segments: Array<{ start: number; end: number; regionFx: FxEffect[] }> = [];
  let cursor = fromTime;
  for (const reg of sorted) {
    const rStart = Math.max(reg.start, fromTime);
    const rEnd   = Math.min(reg.end,   toTime);
    if (rStart > cursor) segments.push({ start: cursor, end: rStart, regionFx: [] });
    segments.push({ start: rStart, end: rEnd, regionFx: reg.effects });
    cursor = rEnd;
  }
  if (cursor < toTime) segments.push({ start: cursor, end: toTime, regionFx: [] });

  // Render each segment independently
  const parts: AudioBuffer[] = [];
  for (const seg of segments) {
    const startSample = Math.round(seg.start * sr);
    const segLen      = Math.max(1, Math.round((seg.end - seg.start) * sr));
    const offCtx      = new OfflineAudioContext(nCh, segLen, sr);
    const slice       = offCtx.createBuffer(nCh, segLen, sr);
    for (let ch = 0; ch < nCh; ch++) {
      const src = buffer.getChannelData(ch);
      const dst = slice.getChannelData(ch);
      for (let i = 0; i < segLen; i++) dst[i] = src[startSample + i] ?? 0;
    }
    const srcNode = offCtx.createBufferSource();
    srcNode.buffer = slice;
    const allFx   = [...globalChain, ...seg.regionFx];
    const segDur  = seg.end - seg.start;
    const outNode = buildFxChain(offCtx, srcNode, allFx, slice, 0, segDur, 0);
    outNode.connect(offCtx.destination);
    srcNode.start(0);
    parts.push(await offCtx.startRendering());
  }

  // Concatenate all parts
  const totalLen = parts.reduce((s, b) => s + b.length, 0);
  const result   = new AudioBuffer({ numberOfChannels: nCh, length: totalLen, sampleRate: sr });
  let offset = 0;
  for (const part of parts) {
    for (let ch = 0; ch < nCh; ch++) result.getChannelData(ch).set(part.getChannelData(ch), offset);
    offset += part.length;
  }
  return result;
}

function audioBufferToWav(ab: AudioBuffer): Blob {
  const nCh = ab.numberOfChannels, sr = ab.sampleRate, len = ab.length;
  const buf = new ArrayBuffer(44 + len * nCh * 2);
  const v = new DataView(buf);
  const ws = (off: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, buf.byteLength - 8, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, nCh, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * nCh * 2, true);
  v.setUint16(32, nCh * 2, true); v.setUint16(34, 16, true);
  ws(36, 'data'); v.setUint32(40, len * nCh * 2, true);
  let offset = 44;
  for (let i = 0; i < len; i++) {
    for (let ch = 0; ch < nCh; ch++) {
      const s = Math.max(-1, Math.min(1, ab.getChannelData(ch)[i]));
      v.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([buf], { type: 'audio/wav' });
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 99999,
    background: 'rgba(0,0,0,0.92)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  } as React.CSSProperties,
  card: {
    width: '92vw', maxWidth: 900, height: '90vh',
    background: '#1a1a2e', borderRadius: 16, display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden', color: '#e2e8f0',
  } as React.CSSProperties,
  header: {
    padding: '14px 20px', background: '#16213e',
    display: 'flex', alignItems: 'center', gap: 12,
    borderBottom: '1px solid #0f3460',
  } as React.CSSProperties,
  tabs: {
    display: 'flex', gap: 4, padding: '10px 16px',
    background: '#16213e', borderBottom: '1px solid #0f3460', flexWrap: 'wrap' as const,
  },
  tab: (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
    fontWeight: active ? 700 : 400,
    background: active ? '#e94560' : '#0f3460',
    color: active ? '#fff' : '#94a3b8',
    transition: 'all 0.15s',
  }),
  body: {
    flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16,
  } as React.CSSProperties,
  footer: {
    padding: '12px 20px', background: '#16213e', borderTop: '1px solid #0f3460',
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  } as React.CSSProperties,
  label: { fontSize: 12, color: '#94a3b8', marginBottom: 4, display: 'block' } as React.CSSProperties,
  input: {
    width: '100%', background: '#0f3460', border: '1px solid #1a4a8a',
    borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 13,
  } as React.CSSProperties,
  textarea: {
    width: '100%', background: '#0f3460', border: '1px solid #1a4a8a',
    borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 13,
    resize: 'vertical' as const, minHeight: 100,
  },
  select: {
    background: '#0f3460', border: '1px solid #1a4a8a',
    borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 13,
  } as React.CSSProperties,
  btn: (variant: 'primary' | 'secondary' | 'danger' | 'success'): React.CSSProperties => ({
    padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: 13, transition: 'opacity 0.15s',
    background: variant === 'primary' ? '#e94560' : variant === 'success' ? '#10b981'
      : variant === 'danger' ? '#ef4444' : '#334155',
    color: '#fff',
  }),
  card2: {
    background: '#0f3460', borderRadius: 10, padding: 16, border: '1px solid #1a4a8a',
  } as React.CSSProperties,
  progress: {
    background: '#16213e', borderRadius: 8, padding: '10px 16px',
    color: '#fbbf24', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
  } as React.CSSProperties,
};

// ── Détection de langue (heuristique, sans lib externe) ──────────────────────
function detectLang(text: string): string | null {
  if (text.trim().length < 8) return null;
  if (/[\u4e00-\u9fff]/.test(text)) return 'zh';
  if (/[\u3040-\u30ff]/.test(text)) return 'ja';
  if (/[\u0400-\u04ff]/.test(text)) return 'ru';
  if (/[\u0600-\u06ff]/.test(text)) return 'ar';

  const words = text.toLowerCase().match(/\b[a-zàâäéèêëîïôùûüç']+\b/g) || [];
  if (words.length < 2) return null;

  const markers: Record<string, string[]> = {
    fr: ['le','la','les','un','une','des','est','et','en','je','tu','il','nous','vous','ils','du','au','que','qui','pas','pour','sur','avec','dans','mais','ou','donc','car','ni','or'],
    en: ['the','a','an','is','are','was','were','and','or','but','in','on','at','to','for','of','with','he','she','it','we','they','you','i','have','has','this','that','are','do'],
    es: ['el','la','los','las','un','una','es','son','y','en','que','no','se','de','por','con','como','yo','tu','él','para','pero','más','muy'],
    de: ['der','die','das','ein','eine','ist','sind','und','in','ich','du','er','wir','sie','mit','zu','auf','nicht','für','von','auch','noch','oder'],
    it: ['il','lo','la','i','gli','le','un','una','è','e','in','che','non','si','di','per','con','io','tu','lui','ma','anche','come','più'],
    pt: ['o','a','os','as','um','uma','é','são','e','em','que','não','se','de','por','com','para','eu','você','ele','mas','mais','muito'],
    nl: ['de','het','een','is','zijn','en','in','dat','van','op','te','niet','ook','met','ik','jij','hij','ze','dit','maar'],
    pl: ['w','i','z','do','na','że','to','się','jak','nie','jest','ale','co','ja','ty','on','po','czy','ten','tak'],
  };

  const scores: Record<string, number> = {};
  for (const [lang, list] of Object.entries(markers)) {
    scores[lang] = words.filter(w => list.includes(w)).length;
  }
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const best = sorted[0];
  if (best[1] === 0 || best[1] / words.length < 0.04) return null;
  return best[0];
}

// ── Composant principal ───────────────────────────────────────────────────────
const AudioEditor: React.FC<AudioEditorProps> = ({ file, onSave, onCancel, defaultTab }) => {
  const [activeTool, setActiveTool] = useState<AudioTool>(defaultTab ?? 'tts');
  const [processing, setProcessing]  = useState(false);
  const [status, setStatus]          = useState('');
  const [resultBlob, setResultBlob]  = useState<Blob | null>(null);
  const [resultUrl, setResultUrl]    = useState<string | null>(null);
  const [resultName, setResultName]  = useState('output.wav');

  // TTS
  const [ttsText, setTtsText]           = useState('');
  const [ttsLang, setTtsLang]           = useState('fr');
  const [ttsSpeaker, setTtsSpeaker]     = useState(XTTS_SPEAKERS[0]);
  const [ttsDetectedLang, setTtsDetectedLang]   = useState<string | null>(null);
  const [ttsAutoTranslate, setTtsAutoTranslate] = useState(false);
  const ttsDetectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);


  // RVC
  const [rvcModels, setRvcModels]         = useState<string[]>([]);
  const [userRvcModels, setUserRvcModels] = useState<{ name: string; path: string }[]>([]);
  const [rvcModel, setRvcModel]           = useState('');
  const [rvcModelPath, setRvcModelPath]   = useState(''); // vide = prédéfini, sinon chemin absolu user
  const [rvcPitch, setRvcPitch]           = useState(0);
  const [rvcF0, setRvcF0]                 = useState('rmvpe');
  const [rvcMode, setRvcMode]             = useState<'audio' | 'text'>('audio');
  const [rvcShowUpload, setRvcShowUpload] = useState(false);
  const [rvcUploadPth, setRvcUploadPth]   = useState<File | null>(null);
  const [rvcUploadIdx, setRvcUploadIdx]   = useState<File | null>(null);
  const [rvcUploadName, setRvcUploadName] = useState('');
  const [rvcUploading, setRvcUploading]   = useState(false);
  const rvcPthRef = useRef<HTMLInputElement>(null);
  const rvcIdxRef = useRef<HTMLInputElement>(null);

  // RVC voix source : modèle RVC ou référence vocale (clone)
  const [rvcVoiceSource, setRvcVoiceSource] = useState<'rvc' | 'clone'>('rvc');
  const [rvcCloneRef, setRvcCloneRef]       = useState<File | null>(null);
  const [rvcCloneRefUrl, setRvcCloneRefUrl] = useState<string | null>(null);
  const [rvcCloneRec, setRvcCloneRec]       = useState(false);
  const [rvcCloneSecs, setRvcCloneSecs]     = useState(0);
  const rvcCloneMR    = useRef<MediaRecorder | null>(null);
  const rvcCloneChunks = useRef<Blob[]>([]);
  const rvcCloneTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const rvcCloneInputRef = useRef<HTMLInputElement>(null);

  // RVC mode texte
  const [rvcText, setRvcText]                     = useState('');
  const [rvcLang, setRvcLang]                     = useState('fr');
  const [rvcDetectedLang, setRvcDetectedLang]     = useState<string | null>(null);
  const [rvcAutoTranslate, setRvcAutoTranslate]   = useState(false);
  const rvcDetectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Demix
  const [demixModel, setDemixModel] = useState('htdemucs');
  const [stems, setStems]           = useState<StemBlob[]>([]);

  // Traduction vocale (SeamlessM4T)
  const [stSrcLang, setStSrcLang] = useState('auto');
  const [stTgtLang, setStTgtLang] = useState('eng');

  // Transcription
  const [transcribeModel, setTranscribeModel]   = useState('base');
  const [transcribeLang, setTranscribeLang]     = useState('auto');
  const [transcribeResult, setTranscribeResult] = useState<{
    text: string; language: string;
    segments: { text: string; start: number; end: number }[];
  } | null>(null);
  const [transcribeTranslateTo, setTranscribeTranslateTo] = useState('en');
  const [transcribeTranslating, setTranscribeTranslating] = useState(false);
  const [transcribeTranslated, setTranscribeTranslated]   = useState<string | null>(null);

  // Modal de nommage
  const [saveModal, setSaveModal] = useState<{ open: boolean; blob: Blob | null; name: string }>({
    open: false, blob: null, name: '',
  });
  const [saveModalInput, setSaveModalInput] = useState('');

  // ── Effets (Effects tab) ───────────────────────────────────────────────────
  const [fxBuffer, setFxBuffer]           = useState<AudioBuffer | null>(null);
  const [fxCurrentTime, setFxCurrentTime] = useState(0);
  const [fxGlobalChain, setFxGlobalChain] = useState<FxEffect[]>([]);
  const [fxRegions, setFxRegions]         = useState<FxRegion[]>([]);
  const [fxSelectedId, setFxSelectedId]   = useState<string | null>(null); // null = global chain
  const [fxNewType, setFxNewType]         = useState('gain');
  const [fxPlaying, setFxPlaying]         = useState(false);
  const [fxLoading, setFxLoading]         = useState(false);
  const [fxExporting, setFxExporting]     = useState(false);
  const wsContainerRef  = useRef<HTMLDivElement>(null);
  const wsRef           = useRef<WaveSurfer | null>(null);
  const wsRegPluginRef  = useRef<any>(null);
  const wsRegCounterRef = useRef(0);
  const fxCtxRef        = useRef<AudioContext | null>(null);
  const fxSourceRef     = useRef<AudioBufferSourceNode | null>(null);
  const fxStartTimeRef  = useRef(0);
  const fxOffsetRef     = useRef(0);

  // Barre de progression
  const [progress, setProgress]           = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
  const headers = { Authorization: `Bearer ${token}` };

  // ── Source audio du fichier courant ──────────────────────────────────────────
  const sourceUrl = file.url || '';
  const hasSource = !!sourceUrl;

  const startProgress = useCallback((estimatedSeconds: number) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setProgress(0);
    setProgressVisible(true);
    progressTimerRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 89) return prev;
        return prev + (89 - prev) * (0.3 / estimatedSeconds);
      });
    }, 300);
  }, []);

  const finishProgress = useCallback(() => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setProgress(100);
    setTimeout(() => { setProgressVisible(false); setProgress(0); }, 800);
  }, []);

  // Détection automatique de la langue — TTS
  useEffect(() => {
    if (ttsDetectTimer.current) clearTimeout(ttsDetectTimer.current);
    ttsDetectTimer.current = setTimeout(() => {
      const detected = detectLang(ttsText);
      setTtsDetectedLang(detected);
      // Auto-sélectionne la langue de synthèse seulement si pas de traduction active
      if (detected && !ttsAutoTranslate) setTtsLang(detected);
    }, 500);
    return () => { if (ttsDetectTimer.current) clearTimeout(ttsDetectTimer.current); };
  }, [ttsText, ttsAutoTranslate]);

  // Détection automatique de la langue — RVC texte
  useEffect(() => {
    if (rvcDetectTimer.current) clearTimeout(rvcDetectTimer.current);
    rvcDetectTimer.current = setTimeout(() => {
      const detected = detectLang(rvcText);
      setRvcDetectedLang(detected);
      if (detected && !rvcAutoTranslate) setRvcLang(detected);
    }, 500);
    return () => { if (rvcDetectTimer.current) clearTimeout(rvcDetectTimer.current); };
  }, [rvcText, rvcAutoTranslate]);

  // Cleanup URLs
  useEffect(() => () => {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    stems.forEach(s => URL.revokeObjectURL(s.url));
  }, [resultUrl, stems]);

  // Init WaveSurfer when effects tab opens (or source changes)
  useEffect(() => {
    if (activeTool !== 'effects' || !hasSource || !wsContainerRef.current) return;
    // Destroy previous instance
    wsRef.current?.destroy();
    wsRef.current = null;
    wsRegPluginRef.current = null;
    wsRegCounterRef.current = 0;
    setFxBuffer(null);
    setFxRegions([]);
    setFxSelectedId(null);
    setFxCurrentTime(0);
    setFxLoading(true);

    const regPlugin = RegionsPlugin.create();
    const tlPlugin  = TimelinePlugin.create({ height: 20 });

    const ws = WaveSurfer.create({
      container:     wsContainerRef.current!,
      waveColor:     '#6366f1',
      progressColor: '#e94560',
      cursorColor:   '#e94560',
      height:        90,
      normalize:     true,
      backend:       'WebAudio',
      plugins:       [regPlugin, tlPlugin],
    });

    ws.load(sourceUrl);

    ws.on('ready', () => {
      const decoded = ws.getDecodedData();
      if (decoded) setFxBuffer(decoded);
      setFxLoading(false);
    });

    // Drag-to-create regions
    regPlugin.enableDragSelection({ drag: true, resize: true });

    regPlugin.on('region-created', (region: any) => {
      const color = REGION_COLORS[wsRegCounterRef.current % REGION_COLORS.length];
      wsRegCounterRef.current++;
      region.setOptions({ color, drag: true, resize: true });
      setFxRegions(prev => [...prev, {
        id:      region.id,
        start:   region.start,
        end:     region.end,
        color,
        label:   `Zone ${prev.length + 1}`,
        effects: [],
      }]);
      setFxSelectedId(region.id);
    });

    regPlugin.on('region-clicked', (region: any, e: MouseEvent) => {
      e.stopPropagation();
      setFxSelectedId(region.id);
    });

    regPlugin.on('region-updated', (region: any) => {
      setFxRegions(prev => prev.map(r =>
        r.id === region.id ? { ...r, start: region.start, end: region.end } : r,
      ));
    });

    ws.on('timeupdate', (t: number) => setFxCurrentTime(t));

    wsRef.current = ws;
    wsRegPluginRef.current = regPlugin;

    return () => { ws.destroy(); wsRef.current = null; };
  }, [activeTool, hasSource, sourceUrl]); /* intentionally omit fxRegions/fxSelectedId */

  // Sync WaveSurfer playhead while playing via Web Audio
  useEffect(() => {
    if (!fxPlaying) return;
    const id = setInterval(() => {
      if (!fxCtxRef.current || !fxBuffer) return;
      const elapsed = fxCtxRef.current.currentTime - fxStartTimeRef.current;
      const t = Math.min(fxOffsetRef.current + elapsed, fxBuffer.duration);
      setFxCurrentTime(t);
      wsRef.current?.seekTo(t / fxBuffer.duration);
      if (t >= fxBuffer.duration) setFxPlaying(false);
    }, 50);
    return () => clearInterval(id);
  }, [fxPlaying, fxBuffer]);

  // Stop Web Audio playback when leaving effects tab
  useEffect(() => {
    if (activeTool !== 'effects') {
      try { fxSourceRef.current?.stop(); } catch {}
      try { if (fxCtxRef.current?.state !== 'closed') fxCtxRef.current?.close(); } catch {}
      fxSourceRef.current = null;
      setFxPlaying(false);
    }
  }, [activeTool]);

  // Enregistrement référence vocale dans onglet RVC
  const startRvcCloneRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const ext = mimeType.startsWith('audio/ogg') ? '.ogg' : '.webm';
      const mr = new MediaRecorder(stream, { mimeType });
      rvcCloneChunks.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) rvcCloneChunks.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(rvcCloneChunks.current, { type: mimeType });
        const f = new File([blob], `ref_${Date.now()}${ext}`, { type: mimeType });
        if (rvcCloneRefUrl) URL.revokeObjectURL(rvcCloneRefUrl);
        setRvcCloneRef(f); setRvcCloneRefUrl(URL.createObjectURL(blob));
        if (rvcCloneTimer.current) clearInterval(rvcCloneTimer.current);
        setRvcCloneSecs(0); setRvcCloneRec(false);
      };
      mr.start();
      rvcCloneMR.current = mr;
      setRvcCloneSecs(0); setRvcCloneRec(true);
      rvcCloneTimer.current = setInterval(() => setRvcCloneSecs(s => s + 1), 1000);
    } catch { alert('Accès au microphone refusé.'); }
  };
  const stopRvcCloneRecording = () => { rvcCloneMR.current?.stop(); if (rvcCloneTimer.current) clearInterval(rvcCloneTimer.current); };

  // Charger modèles RVC (prédéfinis + utilisateur)
  const loadRvcModels = useCallback(() => {
    axios.get('/api/audio/rvc-models', { headers })
      .then(r => {
        const predefined: string[] = r.data.models || [];
        const user: { name: string; path: string }[] = r.data.userModels || [];
        setRvcModels(predefined);
        setUserRvcModels(user);
        // Sélection initiale : premier modèle disponible
        if (!rvcModel) {
          if (predefined[0]) { setRvcModel(predefined[0]); setRvcModelPath(''); }
          else if (user[0])  { setRvcModel(user[0].name); setRvcModelPath(user[0].path); }
        }
      })
      .catch(() => {});
  }, []); // empty deps: headers ref is stable (token doesn't change mid-session)

  useEffect(() => { loadRvcModels(); }, [loadRvcModels]);

  const setResult = useCallback((blob: Blob, name: string) => {
    setResultBlob(blob);
    setResultUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    setResultName(name);
  }, []);

  // ── TTS ───────────────────────────────────────────────────────────────────────
  const handleTTS = async () => {
    if (!ttsText.trim()) return;
    setProcessing(true); setResultBlob(null); setResultUrl(null);
    startProgress(40);

    let textToSpeak = ttsText;
    if (ttsAutoTranslate) {
      setStatus('Traduction en cours…');
      try {
        const { data } = await axios.post('/api/audio/translate',
          { text: ttsText, targetLang: ttsLang },
          { headers, timeout: 0 }
        );
        textToSpeak = data.translated || ttsText;
      } catch { /* fallback texte original */ }
    }
    setStatus('Génération vocale XTTS v2…');
    try {
      const form = new FormData();
      form.append('text', textToSpeak);
      form.append('language', ttsLang);
      form.append('speaker', ttsSpeaker);
      const { data } = await axios.post('/api/audio/tts', form, { headers, responseType: 'blob', timeout: 0 });
      setResult(new Blob([data], { type: 'audio/wav' }), `tts_${Date.now()}.wav`);
    } catch (e: any) { alert('Erreur TTS : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── Demix ─────────────────────────────────────────────────────────────────────
  const handleDemix = async () => {
    if (!hasSource) return;
    setProcessing(true); setStatus('Démixage Demucs (peut prendre 3-5 min)…');
    startProgress(240);
    setStems([]);
    try {
      const srcResp = await fetch(sourceUrl);
      const srcBlob = await srcResp.blob();
      const form = new FormData();
      form.append('audio', srcBlob, file.name || 'track.mp3');
      form.append('model', demixModel);
      const { data } = await axios.post('/api/audio/demix', form, { headers, responseType: 'blob', timeout: 0 });

      // Extraire le ZIP côté browser
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(data);
      const extracted: StemBlob[] = [];
      for (const [name, zipEntry] of Object.entries(zip.files) as [string, import('jszip').JSZipObject][]) {
        if (!zipEntry.dir) {
          const content = await zipEntry.async('blob');
          extracted.push({ name, url: URL.createObjectURL(content), blob: content });
        }
      }
      setStems(extracted);
    } catch (e: any) { alert('Erreur démixage : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── RVC — Source audio ────────────────────────────────────────────────────────
  const handleRVC = async () => {
    if (!hasSource) return;
    setProcessing(true); setResultBlob(null); setResultUrl(null);

    try {
      const srcResp = await fetch(sourceUrl);
      const srcBlob = await srcResp.blob();

      if (rvcVoiceSource === 'clone') {
        // Mode clonage : transcription audio → XTTS clone
        if (!rvcCloneRef) return;
        setStatus('Transcription de l\'audio source…');
        startProgress(150);
        const transcForm = new FormData();
        transcForm.append('audio', srcBlob, file.name || 'input.wav');
        transcForm.append('language', 'auto');
        transcForm.append('model_size', 'base');
        const { data: transcData } = await axios.post('/api/audio/transcribe', transcForm, { headers, timeout: 0 });
        const transcribedText: string = transcData.text || '';
        if (!transcribedText.trim()) throw new Error('Transcription vide — impossible de cloner la voix');

        setStatus('Clonage de voix XTTS v2…');
        startProgress(90);
        const cloneForm = new FormData();
        cloneForm.append('text', transcribedText);
        cloneForm.append('language', transcData.language || rvcLang);
        cloneForm.append('speaker_wav', rvcCloneRef, rvcCloneRef.name);
        const { data } = await axios.post('/api/audio/tts-clone', cloneForm, { headers, responseType: 'blob', timeout: 0 });
        setResult(new Blob([data], { type: 'audio/wav' }), `clone_src_${Date.now()}.wav`);

      } else {
        // Mode RVC
        if (!rvcModel) return;
        setStatus('Conversion de voix RVC (CPU — peut prendre 5-10 min)…');
        startProgress(360);
        const form = new FormData();
        form.append('audio', srcBlob, file.name || 'input.wav');
        form.append('model_name', rvcModel);
        if (rvcModelPath) form.append('model_path', rvcModelPath);
        form.append('pitch_shift', String(rvcPitch));
        form.append('f0_method', rvcF0);
        const { data } = await axios.post('/api/audio/rvc', form, { headers, responseType: 'blob', timeout: 0 });
        setResult(new Blob([data], { type: 'audio/wav' }), `rvc_${Date.now()}.wav`);
      }
    } catch (e: any) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── RVC Texte → Voix ──────────────────────────────────────────────────────────
  const handleRVCText = async () => {
    if (!rvcText.trim()) return;
    if (rvcVoiceSource === 'rvc' && !rvcModel) return;
    if (rvcVoiceSource === 'clone' && !rvcCloneRef) return;
    setProcessing(true); setResultBlob(null); setResultUrl(null);
    startProgress(rvcVoiceSource === 'clone' ? 120 : 480);

    try {
      // Étape 1 : traduction optionnelle
      let textToSpeak = rvcText;
      if (rvcAutoTranslate) {
        setStatus('Traduction en cours…');
        try {
          const { data } = await axios.post('/api/audio/translate',
            { text: rvcText, targetLang: rvcLang },
            { headers, timeout: 0 }
          );
          textToSpeak = data.translated || rvcText;
        } catch { /* fallback texte original */ }
      }

      if (rvcVoiceSource === 'clone') {
        // Clonage direct XTTS
        setStatus('Clonage de voix XTTS v2…');
        const cloneForm = new FormData();
        cloneForm.append('text', textToSpeak);
        cloneForm.append('language', rvcLang);
        cloneForm.append('speaker_wav', rvcCloneRef!, rvcCloneRef!.name);
        const { data } = await axios.post('/api/audio/tts-clone', cloneForm, { headers, responseType: 'blob', timeout: 0 });
        setResult(new Blob([data], { type: 'audio/wav' }), `clone_tts_${Date.now()}.wav`);

      } else {
        // Pipeline TTS neutre → RVC
        setStatus('Synthèse vocale intermédiaire…');
        const ttsForm = new FormData();
        ttsForm.append('text', textToSpeak);
        ttsForm.append('language', rvcLang);
        ttsForm.append('speaker', 'Claribel Dervla');
        const ttsResp = await axios.post('/api/audio/tts', ttsForm, { headers, responseType: 'blob', timeout: 0 });
        const ttsBlob = new Blob([ttsResp.data], { type: 'audio/wav' });

        setStatus('Conversion de voix RVC…');
        const rvcForm = new FormData();
        rvcForm.append('audio', ttsBlob, 'tts_output.wav');
        rvcForm.append('model_name', rvcModel);
        if (rvcModelPath) rvcForm.append('model_path', rvcModelPath);
        rvcForm.append('pitch_shift', String(rvcPitch));
        rvcForm.append('f0_method', rvcF0);
        const { data } = await axios.post('/api/audio/rvc', rvcForm, { headers, responseType: 'blob', timeout: 0 });
        setResult(new Blob([data], { type: 'audio/wav' }), `rvc_tts_${Date.now()}.wav`);
      }
    } catch (e: any) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── Traduction vocale ─────────────────────────────────────────────────────────
  const handleSpeechTranslate = async () => {
    if (!hasSource) return;
    setProcessing(true); setStatus('Traduction vocale SeamlessM4T v2…'); setResultBlob(null); setResultUrl(null);
    startProgress(300);
    try {
      const srcResp = await fetch(sourceUrl);
      const srcBlob = await srcResp.blob();
      const form = new FormData();
      form.append('audio', srcBlob, file.name || 'input.wav');
      form.append('src_lang', stSrcLang);
      form.append('tgt_lang', stTgtLang);
      const { data } = await axios.post('/api/audio/translate-speech', form, {
        headers, responseType: 'blob', timeout: 0,
      });
      setResult(new Blob([data], { type: 'audio/wav' }), `translated_${stTgtLang}_${Date.now()}.wav`);
    } catch (e: any) { alert('Erreur traduction vocale : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── Effets : lecture / export ─────────────────────────────────────────────
  const fxPlay = async () => {
    if (!fxBuffer) return;
    try { fxSourceRef.current?.stop(); } catch {}
    try { fxCtxRef.current?.close(); } catch {}

    const offset = Math.max(0, Math.min(fxBuffer.duration - 0.01, fxCurrentTime));
    setFxExporting(true); // reuse spinner state for "rendering…"
    let processed: AudioBuffer;
    try {
      processed = await renderWithRegionEffects(fxBuffer, fxRegions, fxGlobalChain, offset, fxBuffer.duration);
    } catch (e: any) {
      alert('Erreur rendu : ' + e.message);
      setFxExporting(false);
      return;
    }
    setFxExporting(false);

    const ctx = new AudioContext();
    fxCtxRef.current = ctx;
    const src = ctx.createBufferSource();
    src.buffer = processed;
    src.connect(ctx.destination);
    fxStartTimeRef.current = ctx.currentTime;
    fxOffsetRef.current = offset;
    src.start(0);
    setFxPlaying(true);
    fxSourceRef.current = src;
    src.onended = () => { setFxPlaying(false); setFxCurrentTime(0); wsRef.current?.seekTo(0); };
  };

  const fxStop = () => {
    try { fxSourceRef.current?.stop(); } catch {}
    try { fxCtxRef.current?.close(); } catch {}
    fxSourceRef.current = null;
    setFxPlaying(false);
  };

  const fxExport = async () => {
    if (!fxBuffer) return;
    setFxExporting(true);
    try {
      const processed = await renderWithRegionEffects(fxBuffer, fxRegions, fxGlobalChain, 0, fxBuffer.duration);
      const wavBlob = audioBufferToWav(processed);
      requestSave(wavBlob, file.name.replace(/\.[^.]+$/, '') + '_effets.wav');
    } catch (e: any) {
      alert('Erreur export : ' + e.message);
    } finally {
      setFxExporting(false);
    }
  };

  // Helper: get/set the effect chain for the selected region or global
  const fxActiveChain: FxEffect[] = fxSelectedId
    ? (fxRegions.find(r => r.id === fxSelectedId)?.effects ?? [])
    : fxGlobalChain;

  const fxSetActiveChain = (updater: (prev: FxEffect[]) => FxEffect[]) => {
    if (fxSelectedId) {
      setFxRegions(prev => prev.map(r =>
        r.id === fxSelectedId ? { ...r, effects: updater(r.effects) } : r,
      ));
    } else {
      setFxGlobalChain(updater);
    }
  };

  // ── Transcription ─────────────────────────────────────────────────────────────
  const handleTranscribe = async () => {
    if (!hasSource) return;
    const estimatedSeconds = transcribeModel === 'tiny' ? 30 : transcribeModel === 'base' ? 90
      : transcribeModel === 'small' ? 240 : 480;
    setProcessing(true); setStatus('Transcription WhisperX…'); setTranscribeResult(null); setTranscribeTranslated(null);
    startProgress(estimatedSeconds);
    try {
      const srcResp = await fetch(sourceUrl);
      const srcBlob = await srcResp.blob();
      const form = new FormData();
      form.append('audio', srcBlob, file.name || 'input.wav');
      form.append('language', transcribeLang);
      form.append('model_size', transcribeModel);
      const { data } = await axios.post('/api/audio/transcribe', form, { headers, timeout: 0 });
      setTranscribeResult(data);
    } catch (e: any) { alert('Erreur transcription : ' + (e.response?.data?.error || e.message)); }
    finally { setProcessing(false); setStatus(''); finishProgress(); }
  };

  // ── Traduction du résultat transcription ──────────────────────────────────────
  const handleTranscribeTranslate = async () => {
    if (!transcribeResult?.text) return;
    setTranscribeTranslating(true);
    setTranscribeTranslated(null);
    try {
      const { data } = await axios.post('/api/audio/translate',
        { text: transcribeResult.text, targetLang: transcribeTranslateTo },
        { headers, timeout: 0 }
      );
      setTranscribeTranslated(data.translated || '');
    } catch (e: any) {
      alert('Erreur traduction : ' + (e.response?.data?.error || e.message));
    } finally {
      setTranscribeTranslating(false);
    }
  };

  // ── Gestion modèles RVC utilisateur ──────────────────────────────────────────
  const handleRvcUpload = async () => {
    if (!rvcUploadPth) return;
    setRvcUploading(true);
    try {
      const form = new FormData();
      form.append('model_file', rvcUploadPth, rvcUploadPth.name);
      if (rvcUploadIdx) form.append('index_file', rvcUploadIdx, rvcUploadIdx.name);
      const modelName = rvcUploadName.trim() || rvcUploadPth.name.replace(/\.pth$/i, '');
      form.append('model_name', modelName);
      await axios.post('/api/audio/rvc-models/upload', form, { headers, timeout: 0 });
      setRvcUploadPth(null); setRvcUploadIdx(null); setRvcUploadName('');
      if (rvcPthRef.current) rvcPthRef.current.value = '';
      if (rvcIdxRef.current) rvcIdxRef.current.value = '';
      loadRvcModels();
    } catch (e: any) {
      alert('Erreur upload : ' + (e.response?.data?.error || e.message));
    } finally {
      setRvcUploading(false);
    }
  };

  const handleRvcDelete = async (name: string) => {
    if (!confirm(`Supprimer le modèle "${name}" ?`)) return;
    try {
      await axios.delete(`/api/audio/rvc-models/${encodeURIComponent(name)}`, { headers });
      if (rvcModel === name) { setRvcModel(''); setRvcModelPath(''); }
      loadRvcModels();
    } catch (e: any) {
      alert('Erreur suppression : ' + (e.response?.data?.error || e.message));
    }
  };

  const requestSave = (blob: Blob, suggestedName: string) => {
    setSaveModalInput(suggestedName);
    setSaveModal({ open: true, blob, name: suggestedName });
  };

  const confirmSave = () => {
    if (saveModal.blob) {
      const finalName = saveModalInput.trim() || saveModal.name;
      onSave(saveModal.blob, finalName);
    }
    setSaveModal({ open: false, blob: null, name: '' });
  };

  const handleSave = () => {
    if (resultBlob) requestSave(resultBlob, resultName);
  };

  // ── Barre de progression ──────────────────────────────────────────────────────
  const ProgressBar = () => !progressVisible ? null : (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', animation: progress < 100 ? 'spin 1s linear infinite' : undefined }}>
            {progress < 100 ? '⏳' : '✅'}
          </span>
          {progress < 100 ? status || 'Traitement en cours…' : 'Terminé !'}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: progress === 100 ? '#10b981' : '#94a3b8' }}>
          {Math.round(progress)}%
        </span>
      </div>
      <div style={{ height: 8, background: '#0a1628', borderRadius: 8, overflow: 'hidden', border: '1px solid #1a4a8a' }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: progress === 100
            ? '#10b981'
            : 'linear-gradient(90deg, #6366f1 0%, #e94560 100%)',
          borderRadius: 8,
          transition: 'width 0.3s ease, background 0.4s ease',
          boxShadow: progress < 100 ? '0 0 8px rgba(233,69,96,0.5)' : '0 0 8px rgba(16,185,129,0.5)',
        }} />
      </div>
    </div>
  );

  // ── Render helpers ────────────────────────────────────────────────────────────
  const AudioPlayer = ({ url, label }: { url: string; label?: string }) => (
    <div style={S.card2}>
      {label && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{label}</div>}
      <audio controls src={url} style={{ width: '100%' }} />
    </div>
  );

  const NoSource = () => (
    <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
      <div>Ouvrez un fichier audio depuis le gestionnaire de fichiers</div>
    </div>
  );

  // ── TABS ──────────────────────────────────────────────────────────────────────
  const tabs: { id: AudioTool; icon: string; label: string }[] = [
    { id: 'tts',        icon: '🗣', label: 'Synthèse vocale' },
    { id: 'demix',      icon: '🎚', label: 'Démixage' },
    { id: 'rvc',        icon: '🔄', label: 'Conversion voix' },
    { id: 'transcribe',       icon: '📝', label: 'Audio → Texte' },
    { id: 'speech-translate', icon: '🌐', label: 'Traduction vocale' },
    { id: 'effects', icon: '🎛', label: 'Effets' },
  ];

  return (
    <div style={S.overlay}>
      <div style={S.card}>

        {/* Header */}
        <div style={S.header}>
          <span style={{ fontSize: 22 }}>🎧</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Éditeur Audio</span>
          {file.name && (
            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>— {file.name}</span>
          )}
          <button onClick={onCancel} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={S.tabs}>
          {tabs.map(t => (
            <button key={t.id} style={S.tab(activeTool === t.id)} onClick={() => { setActiveTool(t.id); setResultBlob(null); setResultUrl(null); }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={S.body}>

          {/* Barre de progression */}
          <ProgressBar />

          {/* ── TTS ── */}
          {activeTool === 'tts' && (
            <>
              <div style={S.card2}>
                <label style={S.label}>Texte à synthétiser</label>
                <textarea style={S.textarea} value={ttsText} onChange={e => setTtsText(e.target.value)}
                  placeholder="Entrez le texte à convertir en voix…" rows={4} />
                {ttsDetectedLang && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>Langue détectée :</span>
                    <span style={{ fontSize: 11, background: '#0f3460', border: '1px solid #1a4a8a', borderRadius: 6, padding: '2px 8px', color: '#60a5fa', fontWeight: 600 }}>
                      {LANGUAGES.find(l => l.code === ttsDetectedLang)?.label || ttsDetectedLang}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' as const }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Langue de synthèse</label>
                  <select style={{ ...S.select, width: '100%' }} value={ttsLang} onChange={e => setTtsLang(e.target.value)}>
                    {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
                {/* Toggle traduction automatique */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
                  <div
                    onClick={() => setTtsAutoTranslate(v => !v)}
                    style={{
                      width: 42, height: 22, borderRadius: 11, cursor: 'pointer', position: 'relative',
                      background: ttsAutoTranslate ? '#6366f1' : '#334155',
                      transition: 'background 0.2s', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                      left: ttsAutoTranslate ? 23 : 3,
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: ttsAutoTranslate ? '#a5b4fc' : '#64748b', whiteSpace: 'nowrap' as const }}>
                    Traduire automatiquement
                  </span>
                </div>
                <div style={{ flex: 2 }}>
                  <label style={S.label}>Voix ({XTTS_SPEAKERS.length} disponibles)</label>
                  <select style={{ ...S.select, width: '100%' }} value={ttsSpeaker} onChange={e => setTtsSpeaker(e.target.value)}>
                    {XTTS_SPEAKERS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {ttsAutoTranslate && (
                <div style={{ fontSize: 11, color: '#94a3b8', background: '#0a1628', borderRadius: 6, padding: '6px 10px', border: '1px solid #1a4a8a' }}>
                  🌐 Le texte sera traduit en <strong style={{ color: '#a5b4fc' }}>{LANGUAGES.find(l => l.code === ttsLang)?.label || ttsLang}</strong> avant la synthèse
                </div>
              )}
              <button style={S.btn('primary')} onClick={handleTTS} disabled={processing || !ttsText.trim()}>
                🗣 Générer la voix
              </button>
              {resultUrl && <AudioPlayer url={resultUrl} label="Résultat TTS" />}
            </>
          )}

          {/* ── DEMIX ── */}
          {activeTool === 'demix' && (
            <>
              {hasSource ? (
                <>
                  <AudioPlayer url={sourceUrl} label="Morceau source" />
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                    <div>
                      <label style={S.label}>Modèle Demucs</label>
                      <select style={{ ...S.select, width: 200 }} value={demixModel} onChange={e => setDemixModel(e.target.value)}>
                        <option value="htdemucs">htdemucs (4 stems — recommandé)</option>
                        <option value="htdemucs_ft">htdemucs_ft (fine-tuné)</option>
                        <option value="2stems">2 stems (voix + accompagnement)</option>
                      </select>
                    </div>
                    <button style={S.btn('primary')} onClick={handleDemix} disabled={processing}>
                      🎚 Démixer (3-5 min)
                    </button>
                  </div>
                  {stems.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>
                        ✅ {stems.length} pistes extraites
                      </div>
                      {stems.map(stem => (
                        <div key={stem.name} style={S.card2}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>🎵 {stem.name}</span>
                            <button style={S.btn('success')} onClick={() => requestSave(stem.blob, stem.name)}>
                              💾 Sauvegarder
                            </button>
                          </div>
                          <audio controls src={stem.url} style={{ width: '100%' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <NoSource />}
            </>
          )}

          {/* ── RVC ── */}
          {activeTool === 'rvc' && (
            <>
              {/* Toggle entrée : source audio / texte */}
              <div style={{ display: 'flex', gap: 4, background: '#0f3460', borderRadius: 10, padding: 4 }}>
                {(['audio', 'text'] as const).map(m => (
                  <button key={m} onClick={() => setRvcMode(m)} style={{
                    flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13,
                    fontWeight: rvcMode === m ? 700 : 400,
                    background: rvcMode === m ? '#e94560' : 'transparent',
                    color: rvcMode === m ? '#fff' : '#94a3b8', transition: 'all 0.15s',
                  }}>
                    {m === 'audio' ? '🎵 Source audio' : '✍️ Texte → Voix'}
                  </button>
                ))}
              </div>

              {/* Toggle voix : modèle RVC / référence vocale */}
              <div style={{ display: 'flex', gap: 4, background: '#0f1a30', borderRadius: 10, padding: 4 }}>
                {(['rvc', 'clone'] as const).map(v => (
                  <button key={v} onClick={() => setRvcVoiceSource(v)} style={{
                    flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13,
                    fontWeight: rvcVoiceSource === v ? 700 : 400,
                    background: rvcVoiceSource === v ? '#6366f1' : 'transparent',
                    color: rvcVoiceSource === v ? '#fff' : '#94a3b8', transition: 'all 0.15s',
                  }}>
                    {v === 'rvc' ? '🔄 Modèle RVC' : '🎤 Référence vocale'}
                  </button>
                ))}
              </div>

              {/* ── Voix source : Modèle RVC ── */}
              {rvcVoiceSource === 'rvc' && (rvcModels.length === 0 && userRvcModels.length === 0 ? (
                <div style={{ ...S.card2, color: '#fbbf24', fontSize: 13 }}>
                  ⚠️ Aucun modèle RVC trouvé. Placez des fichiers <code>.pth</code> dans <code>/opt/audio-service/models/</code> ou uploadez le vôtre ci-dessous.
                </div>
              ) : (
                <>
                  {/* Modèle + pitch + F0 */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
                    <div style={{ flex: 2 }}>
                      <label style={S.label}>Modèle de voix</label>
                      <select
                        style={{ ...S.select, width: '100%' }}
                        value={rvcModelPath ? `user:${rvcModel}` : rvcModel}
                        onChange={e => {
                          const val = e.target.value;
                          const u = userRvcModels.find(u => `user:${u.name}` === val);
                          if (u) { setRvcModel(u.name); setRvcModelPath(u.path); }
                          else { setRvcModel(val); setRvcModelPath(''); }
                        }}
                      >
                        {rvcModels.length > 0 && (
                          <optgroup label="── Modèles prédéfinis">
                            {rvcModels.map(m => <option key={m} value={m}>{m}</option>)}
                          </optgroup>
                        )}
                        {userRvcModels.length > 0 && (
                          <optgroup label="── Mes modèles">
                            {userRvcModels.map(u => (
                              <option key={u.name} value={`user:${u.name}`}>⭐ {u.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Tonalité : {rvcPitch > 0 ? '+' : ''}{rvcPitch}</label>
                      <input type="range" min={-12} max={12} value={rvcPitch}
                        onChange={e => setRvcPitch(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#e94560' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Méthode F0</label>
                      <select style={{ ...S.select, width: '100%' }} value={rvcF0} onChange={e => setRvcF0(e.target.value)}>
                        <option value="rmvpe">rmvpe (recommandé)</option>
                        <option value="crepe">crepe</option>
                        <option value="harvest">harvest</option>
                      </select>
                    </div>
                  </div>

                  {/* Panel upload/gestion modèles */}
                  <div>
                    <button style={{ ...S.btn('secondary'), fontSize: 12, padding: '5px 12px' }}
                      onClick={() => setRvcShowUpload(v => !v)}>
                      📂 {rvcShowUpload ? 'Masquer' : 'Gérer mes modèles'}{userRvcModels.length > 0 && ` (${userRvcModels.length})`}
                    </button>
                    {rvcShowUpload && (
                      <div style={{ ...S.card2, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#a5b4fc' }}>⬆️ Uploader un modèle RVC</div>
                        <div style={{ flex: 2 }}>
                          <label style={S.label}>Nom du modèle</label>
                          <input style={S.input} value={rvcUploadName} placeholder="ex: ma_voix_v2"
                            onChange={e => setRvcUploadName(e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
                          <div style={{ flex: 1 }}>
                            <label style={S.label}>Fichier <span style={{ color: '#e94560' }}>*.pth</span> (requis)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button style={{ ...S.btn('secondary'), fontSize: 12, padding: '5px 10px' }}
                                onClick={() => rvcPthRef.current?.click()}>
                                📁 {rvcUploadPth ? rvcUploadPth.name : 'Choisir…'}
                              </button>
                              {rvcUploadPth && <span style={{ fontSize: 11, color: '#10b981' }}>{(rvcUploadPth.size / 1024 / 1024).toFixed(1)} MB</span>}
                              <input ref={rvcPthRef} type="file" accept=".pth" style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files?.[0] || null; setRvcUploadPth(f); if (f && !rvcUploadName.trim()) setRvcUploadName(f.name.replace(/\.pth$/i, '')); }} />
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={S.label}>Fichier <span style={{ color: '#64748b' }}>*.index</span> (optionnel)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button style={{ ...S.btn('secondary'), fontSize: 12, padding: '5px 10px' }}
                                onClick={() => rvcIdxRef.current?.click()}>
                                📁 {rvcUploadIdx ? rvcUploadIdx.name : 'Choisir…'}
                              </button>
                              {rvcUploadIdx && <span style={{ fontSize: 11, color: '#10b981' }}>{(rvcUploadIdx.size / 1024 / 1024).toFixed(1)} MB</span>}
                              <input ref={rvcIdxRef} type="file" accept=".index" style={{ display: 'none' }}
                                onChange={e => setRvcUploadIdx(e.target.files?.[0] || null)} />
                            </div>
                          </div>
                        </div>
                        <button style={S.btn('primary')} onClick={handleRvcUpload} disabled={rvcUploading || !rvcUploadPth}>
                          {rvcUploading ? '⏳ Upload en cours…' : '⬆️ Uploader le modèle'}
                        </button>
                        {userRvcModels.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, borderTop: '1px solid #1a4a8a', paddingTop: 10 }}>
                              Mes modèles ({userRvcModels.length})
                            </div>
                            {userRvcModels.map(u => (
                              <div key={u.name} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', background: '#0a1628', borderRadius: 6, marginBottom: 4,
                                border: rvcModel === u.name && rvcModelPath ? '1px solid #6366f1' : '1px solid #1a4a8a',
                              }}>
                                <span style={{ fontSize: 13, color: rvcModel === u.name && rvcModelPath ? '#a5b4fc' : '#e2e8f0' }}>⭐ {u.name}</span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button style={{ ...S.btn('secondary'), fontSize: 11, padding: '3px 8px' }}
                                    onClick={() => { setRvcModel(u.name); setRvcModelPath(u.path); }}>Sélectionner</button>
                                  <button style={{ ...S.btn('danger'), fontSize: 11, padding: '3px 8px' }}
                                    onClick={() => handleRvcDelete(u.name)}>🗑</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ))}

              {/* ── Voix source : Référence vocale (clone) ── */}
              {rvcVoiceSource === 'clone' && (
                <div style={S.card2}>
                  <label style={S.label}>Audio de référence <span style={{ color: '#94a3b8' }}>(min. 6s recommandé)</span></label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <button style={{ ...S.btn('secondary'), flex: 1, fontSize: 12 }}
                      onClick={() => rvcCloneInputRef.current?.click()} disabled={rvcCloneRec}>
                      📁 Choisir un fichier
                    </button>
                    <button
                      style={{ ...S.btn(rvcCloneRec ? 'danger' : 'primary'), flex: 1, fontSize: 12 }}
                      onClick={rvcCloneRec ? stopRvcCloneRecording : startRvcCloneRecording}>
                      {rvcCloneRec ? `⏹ Arrêter (${rvcCloneSecs}s)` : '🎙 Enregistrer ma voix'}
                    </button>
                  </div>
                  <input ref={rvcCloneInputRef} type="file" accept="audio/*" style={{ display: 'none' }}
                    onChange={e => {
                      const f = e.target.files?.[0] || null;
                      if (f) { if (rvcCloneRefUrl) URL.revokeObjectURL(rvcCloneRefUrl); setRvcCloneRef(f); setRvcCloneRefUrl(URL.createObjectURL(f)); }
                    }} />
                  {rvcCloneRec && (
                    <div style={{ ...S.progress, marginBottom: 8 }}>
                      <span style={{ color: '#ef4444' }}>●</span>
                      Enregistrement… {rvcCloneSecs}s
                      {rvcCloneSecs < 6 && <span style={{ color: '#fbbf24', fontSize: 11, marginLeft: 8 }}>(min. 6s)</span>}
                    </div>
                  )}
                  {rvcCloneRef && !rvcCloneRec && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 12, color: '#10b981', marginBottom: 6 }}>✅ {rvcCloneRef.name} ({(rvcCloneRef.size / 1024).toFixed(0)} KB)</div>
                      {rvcCloneRefUrl && <audio controls src={rvcCloneRefUrl} style={{ width: '100%', height: 36 }} />}
                      <button style={{ ...S.btn('secondary'), fontSize: 11, marginTop: 6, padding: '4px 10px' }}
                        onClick={() => { setRvcCloneRef(null); setRvcCloneRefUrl(null); }}>✕ Supprimer</button>
                    </div>
                  )}
                  {!rvcCloneRef && !rvcCloneRec && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>Enregistrez ou choisissez un fichier audio de la voix à imiter.</div>
                  )}
                </div>
              )}

              {/* ── Mode entrée : Source audio ── */}
              {rvcMode === 'audio' && (
                hasSource ? (
                  <>
                    <AudioPlayer url={sourceUrl} label="Audio source" />
                    {rvcVoiceSource === 'rvc' && (
                      <div style={{ background: '#1a1a0a', border: '1px solid #78350f', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#fbbf24' }}>
                        ⏱ RVC sur CPU — comptez <strong>5 à 15 min</strong> selon la durée. Privilégiez des extraits courts (30s–2min).
                      </div>
                    )}
                    {rvcVoiceSource === 'clone' && (
                      <div style={{ background: '#0a1a0a', border: '1px solid #14532d', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#86efac' }}>
                        ⚙️ Pipeline : Audio source → Whisper (transcription) → XTTS v2 (clonage de voix)
                      </div>
                    )}
                    <button style={S.btn('primary')} onClick={handleRVC}
                      disabled={processing || (rvcVoiceSource === 'rvc' && !rvcModel) || (rvcVoiceSource === 'clone' && !rvcCloneRef)}>
                      {rvcVoiceSource === 'rvc' ? '🔄 Convertir la voix' : '🎤 Cloner la voix'}
                    </button>
                  </>
                ) : <NoSource />
              )}

              {/* ── Mode entrée : Texte → Voix ── */}
              {rvcMode === 'text' && (
                <>
                  <div style={S.card2}>
                    <label style={S.label}>Texte à synthétiser</label>
                    <textarea style={S.textarea} value={rvcText} onChange={e => setRvcText(e.target.value)}
                      placeholder="Entrez le texte à générer avec cette voix…" rows={4} />
                    {rvcDetectedLang && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>Langue détectée :</span>
                        <span style={{ fontSize: 11, background: '#0f3460', border: '1px solid #1a4a8a', borderRadius: 6, padding: '2px 8px', color: '#60a5fa', fontWeight: 600 }}>
                          {LANGUAGES.find(l => l.code === rvcDetectedLang)?.label || rvcDetectedLang}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' as const }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Langue de synthèse</label>
                      <select style={{ ...S.select, width: '100%' }} value={rvcLang} onChange={e => setRvcLang(e.target.value)}>
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
                      <div onClick={() => setRvcAutoTranslate(v => !v)} style={{
                        width: 42, height: 22, borderRadius: 11, cursor: 'pointer', position: 'relative',
                        background: rvcAutoTranslate ? '#6366f1' : '#334155', transition: 'background 0.2s', flexShrink: 0,
                      }}>
                        <div style={{ position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: rvcAutoTranslate ? 23 : 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: rvcAutoTranslate ? '#a5b4fc' : '#64748b', whiteSpace: 'nowrap' as const }}>
                        Traduire automatiquement
                      </span>
                    </div>
                  </div>
                  {rvcAutoTranslate && (
                    <div style={{ fontSize: 11, color: '#94a3b8', background: '#0a1628', borderRadius: 6, padding: '6px 10px', border: '1px solid #1a4a8a' }}>
                      🌐 Le texte sera traduit en <strong style={{ color: '#a5b4fc' }}>{LANGUAGES.find(l => l.code === rvcLang)?.label || rvcLang}</strong> avant la synthèse
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#64748b', background: '#0a1628', borderRadius: 6, padding: '6px 10px', border: '1px solid #1a4a8a' }}>
                    {rvcVoiceSource === 'rvc'
                      ? <>⚙️ Texte → XTTS v2 (voix neutre) → RVC → timbre <strong style={{ color: '#94a3b8' }}>{rvcModel}</strong><br />⏱ 1-2 min (TTS) + 5-15 min (RVC).</>
                      : <>⚙️ Texte → XTTS v2 avec la voix de référence fournie.<br />⏱ ~1-3 min selon la longueur du texte.</>
                    }
                  </div>
                  <button style={S.btn('primary')} onClick={handleRVCText}
                    disabled={processing || !rvcText.trim() || (rvcVoiceSource === 'rvc' && !rvcModel) || (rvcVoiceSource === 'clone' && !rvcCloneRef)}>
                    🎤 Générer avec cette voix
                  </button>
                </>
              )}

              {resultUrl && <AudioPlayer url={resultUrl} label="Résultat" />}
            </>
          )}

          {/* ── EFFETS ── */}
          {activeTool === 'effects' && (
            <>
              {!hasSource ? <NoSource /> : (
                <>
                  {/* ── WaveSurfer container ─────────────────────────────────────── */}
                  <div style={S.card2}>
                    {fxLoading && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>⏳ Décodage audio…</div>
                    )}
                    {/* WaveSurfer mounts here */}
                    <div ref={wsContainerRef} style={{ borderRadius: 6, overflow: 'hidden', background: '#0a1628' }} />
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, textAlign: 'center' }}>
                      Glissez sur la forme d'onde pour créer une zone d'effet
                    </div>
                    {/* Transport */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' as const }}>
                      <button style={S.btn(fxPlaying ? 'danger' : 'primary')}
                        disabled={!fxBuffer || fxExporting}
                        onClick={fxPlaying ? fxStop : fxPlay}>
                        {fxExporting ? '⏳ Rendu…' : fxPlaying ? '⏹ Stop' : '▶ Écouter avec effets'}
                      </button>
                      {fxPlaying && (
                        <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>
                          {fxCurrentTime.toFixed(2)}s / {fxBuffer?.duration.toFixed(2)}s
                        </span>
                      )}
                      <div style={{ marginLeft: 'auto' }}>
                        <button style={S.btn('success')} onClick={fxExport}
                          disabled={!fxBuffer || fxExporting || fxPlaying}>
                          {fxExporting ? '⏳ Rendu…' : '💾 Exporter avec effets'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── Zones (regions) ─────────────────────────────────────────── */}
                  {fxBuffer && (
                    <div style={S.card2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>Zones &amp; effets</span>
                        {/* Global button */}
                        <button
                          style={{
                            ...S.btn(fxSelectedId === null ? 'primary' : 'secondary'),
                            fontSize: 11, padding: '3px 10px',
                          }}
                          onClick={() => setFxSelectedId(null)}>
                          🌐 Globaux {fxGlobalChain.length > 0 && `(${fxGlobalChain.length})`}
                        </button>
                        {/* One button per region */}
                        {fxRegions.map(r => (
                          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button
                              style={{
                                ...S.btn(fxSelectedId === r.id ? 'primary' : 'secondary'),
                                fontSize: 11, padding: '3px 10px',
                                borderLeft: `3px solid ${r.color.replace('0.3', '0.9')}`,
                              }}
                              onClick={() => setFxSelectedId(r.id)}>
                              {r.label} {r.start.toFixed(1)}s→{r.end.toFixed(1)}s
                              {r.effects.length > 0 && ` (${r.effects.length})`}
                            </button>
                            <button
                              title="Supprimer la zone"
                              style={{ ...S.btn('danger'), fontSize: 10, padding: '2px 6px' }}
                              onClick={() => {
                                // remove from WaveSurfer
                                wsRegPluginRef.current?.getRegions().forEach((reg: any) => {
                                  if (reg.id === r.id) reg.remove();
                                });
                                setFxRegions(prev => prev.filter(x => x.id !== r.id));
                                if (fxSelectedId === r.id) setFxSelectedId(null);
                              }}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Effect chain panel ──────────────────────────────────────── */}
                  {fxBuffer && (
                    <div style={S.card2}>
                      {/* Panel title */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>
                          {fxSelectedId
                            ? `Effets — ${fxRegions.find(r => r.id === fxSelectedId)?.label ?? 'Zone'}`
                            : 'Effets globaux (toute la piste)'}
                        </span>
                        {/* Add effect row */}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                          <select style={{ ...S.select }} value={fxNewType} onChange={e => setFxNewType(e.target.value)}>
                            {Object.entries(FX_CATALOG).map(([k, d]) => (
                              <option key={k} value={k}>{d.icon} {d.label}</option>
                            ))}
                          </select>
                          <button style={S.btn('primary')} onClick={() => {
                            const def = FX_CATALOG[fxNewType];
                            const params: Record<string, number> = {};
                            Object.entries(def.params).forEach(([k, p]) => { params[k] = p.default; });
                            fxSetActiveChain(prev => [...prev, {
                              id: Date.now().toString(36) + Math.random().toString(36).slice(2),
                              type: fxNewType, enabled: true, params,
                            }]);
                          }}>➕</button>
                        </div>
                      </div>

                      {fxActiveChain.length === 0 && (
                        <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center', padding: '10px 0', background: '#0a1628', borderRadius: 8, border: '1px dashed #1a4a8a' }}>
                          {fxSelectedId
                            ? 'Aucun effet sur cette zone — ajoutez-en ci-dessus'
                            : 'Aucun effet global — ajoutez-en ci-dessus'}
                        </div>
                      )}

                      {/* Effect chain list */}
                      {fxActiveChain.map((fx, idx) => {
                        const def = FX_CATALOG[fx.type];
                        return (
                          <div key={fx.id} style={{
                            ...S.card2,
                            border: fx.enabled ? '1px solid #6366f1' : '1px solid #334155',
                            opacity: fx.enabled ? 1 : 0.55, transition: 'opacity 0.2s',
                            marginBottom: 8,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: Object.keys(def.params).length ? 10 : 0 }}>
                              <div onClick={() => fxSetActiveChain(prev => prev.map(f => f.id === fx.id ? { ...f, enabled: !f.enabled } : f))}
                                style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', position: 'relative', flexShrink: 0,
                                  background: fx.enabled ? '#6366f1' : '#334155', transition: 'background 0.2s' }}>
                                <div style={{ position: 'absolute', top: 2, width: 14, height: 14, borderRadius: '50%',
                                  background: '#fff', transition: 'left 0.2s', left: fx.enabled ? 15 : 2 }} />
                              </div>
                              <span style={{ fontSize: 14, fontWeight: 700 }}>{def.icon}</span>
                              <span style={{ fontSize: 13, fontWeight: 600 }}>{def.label}</span>
                              <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>#{idx + 1}</span>
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                                <button disabled={idx === 0}
                                  style={{ ...S.btn('secondary'), fontSize: 11, padding: '2px 7px', opacity: idx === 0 ? 0.3 : 1 }}
                                  onClick={() => fxSetActiveChain(prev => { const a = [...prev]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; return a; })}>▲</button>
                                <button disabled={idx === fxActiveChain.length - 1}
                                  style={{ ...S.btn('secondary'), fontSize: 11, padding: '2px 7px', opacity: idx === fxActiveChain.length - 1 ? 0.3 : 1 }}
                                  onClick={() => fxSetActiveChain(prev => { const a = [...prev]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return a; })}>▼</button>
                                <button style={{ ...S.btn('danger'), fontSize: 11, padding: '2px 7px' }}
                                  onClick={() => fxSetActiveChain(prev => prev.filter(f => f.id !== fx.id))}>🗑</button>
                              </div>
                            </div>
                            {Object.entries(def.params).map(([pk, pd]) => (
                              <div key={pk} style={{ marginTop: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{pd.label}</span>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', fontFamily: 'monospace' }}>
                                    {pk === 'gain'
                                      ? Math.round(fx.params[pk] * 100) + '%'
                                      : pk === 'pan'
                                        ? (fx.params[pk] < 0 ? `G ${Math.abs(Math.round(fx.params[pk] * 100))}` : fx.params[pk] > 0 ? `D ${Math.round(fx.params[pk] * 100)}` : 'Centre')
                                        : fx.params[pk].toFixed(pd.step < 1 ? 2 : 0)}
                                  </span>
                                </div>
                                <input type="range" style={{ width: '100%', accentColor: '#6366f1' }}
                                  min={pd.min} max={pd.max} step={pd.step} value={fx.params[pk]}
                                  onChange={e => fxSetActiveChain(prev => prev.map(f =>
                                    f.id === fx.id ? { ...f, params: { ...f.params, [pk]: Number(e.target.value) } } : f,
                                  ))} />
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── TRANSCRIPTION ── */}
          {activeTool === 'transcribe' && (
            <>
              {hasSource ? (
                <>
                  <AudioPlayer url={sourceUrl} label="Audio source" />
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Modèle Whisper</label>
                      <select style={{ ...S.select, width: '100%' }} value={transcribeModel} onChange={e => setTranscribeModel(e.target.value)}>
                        <option value="tiny">tiny — très rapide, moins précis</option>
                        <option value="base">base — rapide, bon équilibre (recommandé)</option>
                        <option value="small">small — précis, plus lent</option>
                        <option value="medium">medium — très précis, lent</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Langue (auto = détection)</label>
                      <select style={{ ...S.select, width: '100%' }} value={transcribeLang} onChange={e => setTranscribeLang(e.target.value)}>
                        <option value="auto">🌐 Détection automatique</option>
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <button style={S.btn('primary')} onClick={handleTranscribe} disabled={processing}>
                    📝 Transcrire l'audio
                  </button>

                  {transcribeResult && (
                    <div style={S.card2}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          ✅ Transcription — Langue détectée : <span style={{ color: '#60a5fa' }}>
                            {LANGUAGES.find(l => l.code === transcribeResult.language)?.label || transcribeResult.language}
                          </span>
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button style={S.btn('secondary')} onClick={() => navigator.clipboard.writeText(transcribeResult.text)}>
                            📋 Copier
                          </button>
                          <button style={S.btn('success')} onClick={() => {
                            const blob = new Blob([transcribeResult.text], { type: 'text/plain' });
                            requestSave(blob, file.name.replace(/\.[^.]+$/, '') + '_transcription.txt');
                          }}>
                            💾 Sauvegarder .txt
                          </button>
                        </div>
                      </div>
                      <textarea
                        style={{ ...S.textarea, minHeight: 140, color: '#e2e8f0' }}
                        value={transcribeResult.text}
                        onChange={e => setTranscribeResult(prev => prev ? { ...prev, text: e.target.value } : null)}
                        rows={6}
                      />
                      {transcribeResult.segments.length > 1 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                            {transcribeResult.segments.length} segments horodatés
                          </div>
                          <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12 }}>
                            {transcribeResult.segments.map((seg, i) => (
                              <div key={i} style={{ display: 'flex', gap: 10, padding: '4px 0', borderBottom: '1px solid #0f3460' }}>
                                <span style={{ color: '#10b981', minWidth: 70, flexShrink: 0 }}>
                                  {seg.start.toFixed(1)}s → {seg.end.toFixed(1)}s
                                </span>
                                <span>{seg.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Bloc traduction */}
                      <div style={{ marginTop: 14, borderTop: '1px solid #1a4a8a', paddingTop: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
                          <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>🌐 Traduire en :</span>
                          <select
                            style={{ ...S.select, flex: 1, minWidth: 140 }}
                            value={transcribeTranslateTo}
                            onChange={e => { setTranscribeTranslateTo(e.target.value); setTranscribeTranslated(null); }}
                          >
                            {LANGUAGES.filter(l => l.code !== transcribeResult.language).map(l => (
                              <option key={l.code} value={l.code}>{l.label}</option>
                            ))}
                          </select>
                          <button
                            style={S.btn('primary')}
                            onClick={handleTranscribeTranslate}
                            disabled={transcribeTranslating || processing}
                          >
                            {transcribeTranslating ? '⏳ Traduction…' : '🌐 Traduire'}
                          </button>
                          {transcribeTranslated && (
                            <>
                              <button style={S.btn('secondary')} onClick={() => navigator.clipboard.writeText(transcribeTranslated)}>
                                📋 Copier
                              </button>
                              <button style={S.btn('success')} onClick={() => {
                                const blob = new Blob([transcribeTranslated], { type: 'text/plain' });
                                requestSave(blob, file.name.replace(/\.[^.]+$/, '') + `_${transcribeTranslateTo}.txt`);
                              }}>
                                💾 Sauvegarder
                              </button>
                            </>
                          )}
                        </div>
                        {transcribeTranslated && (
                          <textarea
                            style={{ ...S.textarea, minHeight: 100, marginTop: 10, color: '#a5b4fc' }}
                            value={transcribeTranslated}
                            onChange={e => setTranscribeTranslated(e.target.value)}
                            rows={4}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : <NoSource />}
            </>
          )}

          {/* ── TRADUCTION VOCALE ── */}
          {activeTool === 'speech-translate' && (
            <>
              {hasSource ? (
                <>
                  <AudioPlayer url={sourceUrl} label="Audio source" />

                  <div style={{ background: '#0a1a0a', border: '1px solid #14532d', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#86efac' }}>
                    🤖 <strong>SeamlessM4T v2</strong> (Meta) — traduction directe parole-vers-parole sans passer par du texte. Voix neutre générée. Supporte ~100 langues.
                    <br /><span style={{ color: '#6ee7b7', marginTop: 4, display: 'block' }}>⏱ Lent sur CPU — comptez 2 à 5 min/minute d'audio. Privilégiez des extraits courts.</span>
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Langue source</label>
                      <select style={{ ...S.select, width: '100%' }} value={stSrcLang} onChange={e => setStSrcLang(e.target.value)}>
                        <option value="auto">🌐 Détection automatique</option>
                        {SEAMLESS_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={S.label}>Langue cible</label>
                      <select style={{ ...S.select, width: '100%' }} value={stTgtLang} onChange={e => setStTgtLang(e.target.value)}>
                        {SEAMLESS_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <button style={S.btn('primary')} onClick={handleSpeechTranslate} disabled={processing || stSrcLang === stTgtLang}>
                    🌐 Traduire la voix
                  </button>
                  {stSrcLang === stTgtLang && (
                    <div style={{ fontSize: 11, color: '#ef4444' }}>La langue source et cible doivent être différentes.</div>
                  )}
                  {resultUrl && <AudioPlayer url={resultUrl} label={`Résultat — ${SEAMLESS_LANGUAGES.find(l => l.code === stTgtLang)?.label || stTgtLang}`} />}
                </>
              ) : <NoSource />}
            </>
          )}

        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button style={S.btn('secondary')} onClick={onCancel}>Fermer</button>
          {resultBlob && activeTool !== 'demix' && (
            <button style={S.btn('success')} onClick={handleSave}>
              💾 Sauvegarder dans mes fichiers
            </button>
          )}
        </div>

      </div>

      {/* Modal nommage fichier */}
      {saveModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100000,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#1a1a2e', borderRadius: 14, padding: 28, width: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.7)', border: '1px solid #0f3460',
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#e2e8f0' }}>
              💾 Nommer le fichier
            </div>
            <label style={S.label}>Nom du fichier</label>
            <input
              style={{ ...S.input, marginBottom: 20 }}
              value={saveModalInput}
              onChange={e => setSaveModalInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmSave(); if (e.key === 'Escape') setSaveModal({ open: false, blob: null, name: '' }); }}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={S.btn('secondary')} onClick={() => setSaveModal({ open: false, blob: null, name: '' })}>
                Annuler
              </button>
              <button style={S.btn('success')} onClick={confirmSave} disabled={!saveModalInput.trim()}>
                💾 Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AudioEditor;
